import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useAuthContext } from '../../context/auth-context';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  AutoFixHigh,
  CheckCircle,
  Delete,
  FileDownload,
  KeyboardArrowDown,
  KeyboardArrowUp,
  DeleteOutline,
  MoreVert,
  People,
  Search,
  Undo,
  UploadFile,
  Warning,
} from '@mui/icons-material';
import styled from 'styled-components';
import { eventUrl, rubricUrl, evenTypeUrl, baseUrls } from '../../constants/';
import { useGenerateGradesMutation, useGetGenerateGradesStatusQuery } from '../../redux/services/grade-api';
import { useIsMobile } from '../../hooks/use-is-mobile';

const FIXED_LEAD_JUDGE_ID = '647ffb469bc9e3f5195de3bd';
const SEARCH_DEBOUNCE_MS = 400;

const StyledTableContainer = styled(TableContainer)`
  margin-top: 2%;
`;

const ExpandableRow = styled(TableRow)`
  & > * {
    border-bottom: unset;
  }
`;

const RubricsTable = styled(Table)`
  background-color: #f5f5f5;
`;

const ValidationIcon = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  flex-wrap: wrap;
`;

const EmptyState = styled(Box)`
  padding: 16px;
`;

const buildEventPayload = (values, campId) => ({
  name: values.name.trim(),
  event_type: values.event_type,
  camp: campId,
  leadJudge: FIXED_LEAD_JUDGE_ID,
  status: values.status,
  evaluation_type: values.evaluation_type,
  event_category: values.event_category,
  maxPointEvents: Number(values.maxPointEvents),
  rubricsCustomOrder: values.rubricsCustomOrder,
});

const getIncludedRubricsSum = (rubrics = []) => {
  return rubrics
    .filter(item => item.includedInSum)
    .reduce((sum, item) => sum + Number(item.maxPoints || 0), 0);
};

const normalizeRubrics = (rubrics = [], customOrder = false) => {
  if (customOrder) {
    return [...rubrics].sort((a, b) => {
      const left = Number.isInteger(a.displayedOrder) ? a.displayedOrder : Number.MAX_SAFE_INTEGER;
      const right = Number.isInteger(b.displayedOrder) ? b.displayedOrder : Number.MAX_SAFE_INTEGER;
      return left - right;
    });
  }

  return [...rubrics].sort((a, b) => a.name.localeCompare(b.name));
};

const getCampIdFromEvent = event => {
  if (!event?.camp) return null;
  if (typeof event.camp === 'string') return event.camp;
  return event.camp._id;
};

const EventDialog = ({
  open,
  mode,
  value,
  eventTypes,
  evaluationTypes,
  eventCategories,
  onClose,
  onSave,
  saving,
}) => {
  const [form, setForm] = useState(value);
  const isMobile = useIsMobile();
  const [initialRubricDraft, setInitialRubricDraft] = useState({
    name: '',
    maxPoints: '',
    includedInSum: true,
    allowNegative: false,
    instructions: '',
  });

  useEffect(() => {
    setForm(value);
  }, [value]);

  useEffect(() => {
    setInitialRubricDraft({
      name: '',
      maxPoints: '',
      includedInSum: true,
      allowNegative: false,
      instructions: '',
    });
  }, [open, mode]);

  const createMode = mode === 'create';
  const currentIncludedRubricSum = createMode
    ? getIncludedRubricsSum(form.rubrics || [])
    : Number(form.existingIncludedRubricsSum || 0);
  const currentIncludedRubricCount = createMode
    ? (form.rubrics || []).filter(item => item.includedInSum).length
    : Number(form.includedRubricsCount || 0);

  const hasRubricsIncludedInSum = currentIncludedRubricCount > 0;
  const isRubricTotalValid = !hasRubricsIncludedInSum
    || currentIncludedRubricSum === Number(form.maxPointEvents || 0);
  const areCreateRubricsValid = !createMode || (form.rubrics || []).every(item => (
    item.name?.trim()?.length > 1 && Number.isFinite(Number(item.maxPoints))
  ));

  const canSave = useMemo(() => {
    return (
      form.name.trim().length > 1
      && form.event_type
      && form.evaluation_type
      && form.event_category
      && String(form.maxPointEvents).trim() !== ''
      && Number.isFinite(Number(form.maxPointEvents))
      && Number(form.maxPointEvents) >= 0
      && isRubricTotalValid
      && areCreateRubricsValid
    );
  }, [form, isRubricTotalValid, areCreateRubricsValid]);

  const handleChange = key => event => {
    const val = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(curr => ({ ...curr, [key]: val }));
  };

  const handleInitialRubricDraftChange = key => event => {
    const val = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setInitialRubricDraft(curr => ({ ...curr, [key]: val }));
  };

  const canAddInitialRubric = useMemo(() => {
    return initialRubricDraft.name.trim().length > 1
      && String(initialRubricDraft.maxPoints).trim() !== ''
      && Number.isFinite(Number(initialRubricDraft.maxPoints));
  }, [initialRubricDraft]);

  const addInitialRubric = () => {
    if (!canAddInitialRubric) return;

    const next = {
      localId: `${Date.now()}-${Math.random()}`,
      name: initialRubricDraft.name.trim(),
      maxPoints: Number(initialRubricDraft.maxPoints),
      includedInSum: Boolean(initialRubricDraft.includedInSum),
      allowNegative: Boolean(initialRubricDraft.allowNegative),
      instructions: initialRubricDraft.instructions || '',
    };

    setForm(curr => ({
      ...curr,
      rubrics: [...(curr.rubrics || []), next],
    }));

    setInitialRubricDraft({
      name: '',
      maxPoints: '',
      includedInSum: true,
      allowNegative: false,
      instructions: '',
    });
  };

  const removeInitialRubric = localId => {
    setForm(curr => ({
      ...curr,
      rubrics: (curr.rubrics || []).filter(item => item.localId !== localId),
    }));
  };

  const updateInitialRubric = (localId, key, value) => {
    setForm(curr => ({
      ...curr,
      rubrics: (curr.rubrics || []).map(item => {
        if (item.localId !== localId) return item;
        return { ...item, [key]: value };
      }),
    }));
  };

  const moveInitialRubric = (index, direction) => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const rubrics = [...(form.rubrics || [])];

    if (targetIndex < 0 || targetIndex >= rubrics.length) return;

    const current = rubrics[index];
    rubrics[index] = rubrics[targetIndex];
    rubrics[targetIndex] = current;

    setForm(curr => ({
      ...curr,
      rubrics,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm' fullScreen={isMobile}>
      <DialogTitle>{mode === 'create' ? 'Crear evento' : 'Editar evento'}</DialogTitle>
      <DialogContent dividers>
        <Box display='grid' gridTemplateColumns='1fr' gap={2} mt={1}>
          <TextField
            label='Nombre del evento'
            value={form.name}
            onChange={handleChange('name')}
            required
            fullWidth
          />

          <FormControl fullWidth required>
            <InputLabel id='event-type-label'>Tipo de evento</InputLabel>
            <Select
              labelId='event-type-label'
              label='Tipo de evento'
              value={form.event_type}
              onChange={handleChange('event_type')}
            >
              {eventTypes.map(item => (
                <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel id='evaluation-type-label'>Tipo de evaluacion</InputLabel>
            <Select
              labelId='evaluation-type-label'
              label='Tipo de evaluacion'
              value={form.evaluation_type}
              onChange={handleChange('evaluation_type')}
            >
              {evaluationTypes.map(item => (
                <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel id='event-category-label'>Categoria de evento</InputLabel>
            <Select
              labelId='event-category-label'
              label='Categoria de evento'
              value={form.event_category}
              onChange={handleChange('event_category')}
            >
              {eventCategories.map(item => (
                <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label='Maximo de puntos del evento'
            type='number'
            value={form.maxPointEvents}
            onChange={handleChange('maxPointEvents')}
            inputProps={{ min: 0, step: 1 }}
            required
            fullWidth
          />

          <FormControl fullWidth required>
            <InputLabel id='status-label'>Estado</InputLabel>
            <Select
              labelId='status-label'
              label='Estado'
              value={form.status}
              onChange={handleChange('status')}
            >
              <MenuItem value={true}>Activo</MenuItem>
              <MenuItem value={false}>Inactivo</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Checkbox checked={form.rubricsCustomOrder} onChange={handleChange('rubricsCustomOrder')} />}
            label='Habilitar orden personalizado de rubros'
          />

          {!isRubricTotalValid ? (
            <Alert severity='error'>
              El maximo del evento ({Number(form.maxPointEvents || 0)}) debe coincidir con la suma de rubros incluidos ({currentIncludedRubricSum}).
            </Alert>
          ) : null}

          <Alert severity='info'>
            {hasRubricsIncludedInSum
              ? `Rubros incluidos en suma: ${currentIncludedRubricCount}. Suma actual: ${currentIncludedRubricSum}.`
              : 'No hay rubros incluidos en la suma.'}
          </Alert>

          {createMode ? (
            <>
              <Typography variant='subtitle1' fontWeight={700}>Rubros iniciales del evento</Typography>
              <Box display='grid' gridTemplateColumns='2fr 1fr' gap={1}>
                <TextField
                  label='Nombre del rubro'
                  value={initialRubricDraft.name}
                  onChange={handleInitialRubricDraftChange('name')}
                  fullWidth
                />
                <TextField
                  label='Puntaje maximo'
                  type='number'
                  value={initialRubricDraft.maxPoints}
                  onChange={handleInitialRubricDraftChange('maxPoints')}
                  fullWidth
                />
              </Box>
              <Box display='grid' gridTemplateColumns='1fr 1fr auto' gap={1} alignItems='center'>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(initialRubricDraft.includedInSum)}
                      onChange={handleInitialRubricDraftChange('includedInSum')}
                    />
                  }
                  label='Incluido en suma'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(initialRubricDraft.allowNegative)}
                      onChange={handleInitialRubricDraftChange('allowNegative')}
                    />
                  }
                  label='Permite negativo'
                />
                <Button variant='outlined' onClick={addInitialRubric} disabled={!canAddInitialRubric}>
                  Agregar rubro
                </Button>
              </Box>
              <TextField
                label='Instrucciones (opcional)'
                value={initialRubricDraft.instructions}
                onChange={handleInitialRubricDraftChange('instructions')}
                multiline
                minRows={2}
                fullWidth
              />

              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell width={70}><strong>Ord.</strong></TableCell>
                    <TableCell><strong>Nombre</strong></TableCell>
                    <TableCell width={120}><strong>Max.</strong></TableCell>
                    <TableCell width={130}><strong>Incluye</strong></TableCell>
                    <TableCell width={130}><strong>Negativo</strong></TableCell>
                    {form.rubricsCustomOrder ? <TableCell width={120}><strong>Orden</strong></TableCell> : null}
                    <TableCell align='right' width={100}><strong>Accion</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(form.rubrics || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={form.rubricsCustomOrder ? 7 : 6} align='center'>
                        Agrega al menos un rubro inicial
                      </TableCell>
                    </TableRow>
                  ) : (form.rubrics || []).map((item, index) => (
                    <TableRow key={item.localId}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <TextField
                          value={item.name}
                          onChange={event => updateInitialRubric(item.localId, 'name', event.target.value)}
                          size='small'
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={item.maxPoints}
                          type='number'
                          onChange={event => updateInitialRubric(item.localId, 'maxPoints', Number(event.target.value))}
                          size='small'
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={Boolean(item.includedInSum)}
                          onChange={event => updateInitialRubric(item.localId, 'includedInSum', event.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={Boolean(item.allowNegative)}
                          onChange={event => updateInitialRubric(item.localId, 'allowNegative', event.target.checked)}
                        />
                      </TableCell>
                      {form.rubricsCustomOrder ? (
                        <TableCell>
                          <Box display='flex' gap={0.5}>
                            <IconButton size='small' onClick={() => moveInitialRubric(index, 'up')} disabled={index === 0}>
                              <ArrowUpward fontSize='inherit' />
                            </IconButton>
                            <IconButton
                              size='small'
                              onClick={() => moveInitialRubric(index, 'down')}
                              disabled={index === (form.rubrics || []).length - 1}
                            >
                              <ArrowDownward fontSize='inherit' />
                            </IconButton>
                          </Box>
                        </TableCell>
                      ) : null}
                      <TableCell align='right'>
                        <IconButton size='small' color='error' onClick={() => removeInitialRubric(item.localId)}>
                          <Delete fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          onClick={() => onSave(form)}
          variant='contained'
          disabled={!canSave || saving}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PRIMARY_COLOR = '#0F6E56';

const WizardProgress = ({ step }) => (
  <Box display='flex' alignItems='center' justifyContent='center' my={1.5}>
    {[0, 1, 2].flatMap((i) => {
      const items = [
        <Box
          key={`dot-${i}`}
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: i <= step ? PRIMARY_COLOR : '#e0e0e0',
            color: i <= step ? '#fff' : '#9e9e9e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
            transition: 'background-color 0.3s',
          }}
        >
          {i + 1}
        </Box>,
      ];
      if (i < 2) {
        items.push(
          <Box
            key={`line-${i}`}
            sx={{
              height: 3,
              width: 56,
              bgcolor: i < step ? PRIMARY_COLOR : '#e0e0e0',
              transition: 'background-color 0.3s',
            }}
          />
        );
      }
      return items;
    })}
  </Box>
);

const WIZARD_STEPS = ['Información general', 'Rubros del evento', 'Revisar y guardar'];

const CreateEventWizard = ({ open, value, eventTypes, evaluationTypes, eventCategories, onClose, onSave, saving }) => {
  const [form, setForm] = useState(value);
  const [step, setStep] = useState(0);
  const isMobile = useIsMobile();
  const [savedOk, setSavedOk] = useState(false);
  const [step1Errors, setStep1Errors] = useState({});
  const [warnNoRubrics, setWarnNoRubrics] = useState(false);
  const [rubricDraft, setRubricDraft] = useState({ name: '', maxPoints: '', includedInSum: true, allowNegative: false, instructions: '' });

  useEffect(() => { setForm(value); }, [value]);

  useEffect(() => {
    if (open) {
      setStep(0);
      setSavedOk(false);
      setStep1Errors({});
      setWarnNoRubrics(false);
      setRubricDraft({ name: '', maxPoints: '', includedInSum: true, allowNegative: false, instructions: '' });
    }
  }, [open]);

  const includedSum = getIncludedRubricsSum(form.rubrics || []);
  const includedCount = (form.rubrics || []).filter(r => r.includedInSum).length;
  const hasIncluded = includedCount > 0;
  const rubricTotalValid = !hasIncluded || includedSum === Number(form.maxPointEvents || 0);
  const rubricsDataValid = (form.rubrics || []).every(r => r.name?.trim()?.length > 1 && Number.isFinite(Number(r.maxPoints)));

  const canSaveForm = useMemo(() => (
    form.name.trim().length > 1
    && form.event_type
    && form.evaluation_type
    && form.event_category
    && String(form.maxPointEvents).trim() !== ''
    && Number.isFinite(Number(form.maxPointEvents))
    && Number(form.maxPointEvents) >= 0
    && rubricTotalValid
    && rubricsDataValid
  ), [form, rubricTotalValid, rubricsDataValid]);

  const handleChange = key => event => {
    const val = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(curr => ({ ...curr, [key]: val }));
    if (step1Errors[key]) setStep1Errors(curr => { const next = { ...curr }; delete next[key]; return next; });
  };

  const canAddRubric = rubricDraft.name.trim().length > 1
    && String(rubricDraft.maxPoints).trim() !== ''
    && Number.isFinite(Number(rubricDraft.maxPoints));

  const addRubric = () => {
    if (!canAddRubric) return;
    setForm(curr => ({
      ...curr,
      rubrics: [...(curr.rubrics || []), {
        localId: `${Date.now()}-${Math.random()}`,
        name: rubricDraft.name.trim(),
        maxPoints: Number(rubricDraft.maxPoints),
        includedInSum: Boolean(rubricDraft.includedInSum),
        allowNegative: Boolean(rubricDraft.allowNegative),
        instructions: rubricDraft.instructions || '',
      }],
    }));
    setRubricDraft({ name: '', maxPoints: '', includedInSum: true, allowNegative: false, instructions: '' });
  };

  const removeRubric = localId => setForm(curr => ({ ...curr, rubrics: (curr.rubrics || []).filter(r => r.localId !== localId) }));

  const updateRubricField = (localId, key, val) => setForm(curr => ({
    ...curr,
    rubrics: (curr.rubrics || []).map(r => r.localId !== localId ? r : { ...r, [key]: val }),
  }));

  const validateStep1 = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = 'Nombre requerido (mín. 2 caracteres)';
    if (!form.event_type) errs.event_type = 'Selecciona un tipo de evento';
    if (!form.event_category) errs.event_category = 'Selecciona una categoría';
    if (!form.evaluation_type) errs.evaluation_type = 'Selecciona un tipo de evaluación';
    if (String(form.maxPointEvents).trim() === '' || !Number.isFinite(Number(form.maxPointEvents)) || Number(form.maxPointEvents) < 0) {
      errs.maxPointEvents = 'Ingresa un máximo de puntos válido (≥ 0)';
    }
    return errs;
  };

  const handleNext = () => {
    if (step === 0) {
      const errs = validateStep1();
      if (Object.keys(errs).length > 0) { setStep1Errors(errs); return; }
      setStep1Errors({});
    }
    if (step === 1) setWarnNoRubrics((form.rubrics || []).length === 0);
    setStep(s => s + 1);
  };

  const handleBack = () => { setWarnNoRubrics(false); setStep(s => s - 1); };

  const handleCancel = () => {
    const dirty = form.name.trim().length > 0 || (form.rubrics || []).length > 0;
    // eslint-disable-next-line no-alert
    if (dirty && !window.confirm('¿Deseas cancelar? Se perderán los datos ingresados.')) return;
    onClose();
  };

  const handleSave = async () => {
    const success = await onSave(form);
    if (success) setSavedOk(true);
  };

  const handleCreateAnother = () => {
    setForm({ name: '', event_type: '', evaluation_type: '', event_category: '', maxPointEvents: 0, status: true, rubricsCustomOrder: false, rubrics: [] });
    setStep(0);
    setSavedOk(false);
    setStep1Errors({});
    setWarnNoRubrics(false);
    setRubricDraft({ name: '', maxPoints: '', includedInSum: true, allowNegative: false, instructions: '' });
  };

  const eventTypeName = eventTypes.find(t => t._id === form.event_type)?.name || '-';
  const evaluationTypeName = evaluationTypes.find(t => t._id === form.evaluation_type)?.name || '-';
  const eventCategoryName = eventCategories.find(t => t._id === form.event_category)?.name || '-';

  return (
    <Dialog open={open} onClose={savedOk ? undefined : handleCancel} fullWidth maxWidth='sm' fullScreen={isMobile}>
      {!savedOk && (
        <DialogTitle sx={{ pb: 0 }}>
          <Typography variant='h6' fontWeight={700}>Crear evento</Typography>
          <WizardProgress step={step} />
          <Typography variant='caption' color='text.secondary' display='block' textAlign='center' mb={0.5}>
            Paso {step + 1} de 3 — {WIZARD_STEPS[step]}
          </Typography>
        </DialogTitle>
      )}

      <DialogContent dividers sx={{ minHeight: 340 }}>
        {savedOk ? (
          <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' py={5} gap={2}>
            <CheckCircle sx={{ fontSize: 72, color: PRIMARY_COLOR }} />
            <Typography variant='h6' fontWeight={700}>¡Evento creado exitosamente!</Typography>
            <Typography variant='body2' color='text.secondary' textAlign='center'>
              El evento <strong>{form.name}</strong> ha sido guardado correctamente.
            </Typography>
            <Box display='flex' gap={2} mt={1}>
              <Button variant='outlined' onClick={handleCreateAnother} sx={{ color: PRIMARY_COLOR, borderColor: PRIMARY_COLOR }}>
                Crear otro evento
              </Button>
              <Button variant='contained' onClick={onClose} sx={{ bgcolor: PRIMARY_COLOR, '&:hover': { bgcolor: '#0a5241' } }}>
                Cerrar
              </Button>
            </Box>
          </Box>
        ) : step === 0 ? (
          <Box display='grid' gridTemplateColumns='1fr' gap={2} mt={1}>
            <TextField
              label='Nombre del evento'
              value={form.name}
              onChange={handleChange('name')}
              required
              fullWidth
              error={Boolean(step1Errors.name)}
              helperText={step1Errors.name}
            />
            <FormControl fullWidth required error={Boolean(step1Errors.event_type)}>
              <InputLabel>Tipo de evento</InputLabel>
              <Select label='Tipo de evento' value={form.event_type} onChange={handleChange('event_type')}>
                {eventTypes.map(item => <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>)}
              </Select>
              {step1Errors.event_type && <Typography variant='caption' color='error' sx={{ ml: 1.5, mt: 0.5 }}>{step1Errors.event_type}</Typography>}
            </FormControl>
            <FormControl fullWidth required error={Boolean(step1Errors.event_category)}>
              <InputLabel>Categoría de evento</InputLabel>
              <Select label='Categoría de evento' value={form.event_category} onChange={handleChange('event_category')}>
                {eventCategories.map(item => <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>)}
              </Select>
              {step1Errors.event_category && <Typography variant='caption' color='error' sx={{ ml: 1.5, mt: 0.5 }}>{step1Errors.event_category}</Typography>}
            </FormControl>
            <FormControl fullWidth required error={Boolean(step1Errors.evaluation_type)}>
              <InputLabel>Tipo de evaluación</InputLabel>
              <Select label='Tipo de evaluación' value={form.evaluation_type} onChange={handleChange('evaluation_type')}>
                {evaluationTypes.map(item => <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>)}
              </Select>
              {step1Errors.evaluation_type && <Typography variant='caption' color='error' sx={{ ml: 1.5, mt: 0.5 }}>{step1Errors.evaluation_type}</Typography>}
            </FormControl>
            <TextField
              label='Máximo de puntos del evento'
              type='number'
              value={form.maxPointEvents}
              onChange={handleChange('maxPointEvents')}
              inputProps={{ min: 0, step: 1 }}
              required
              fullWidth
              error={Boolean(step1Errors.maxPointEvents)}
              helperText={step1Errors.maxPointEvents}
            />
            <FormControl fullWidth>
              <InputLabel>Estado inicial</InputLabel>
              <Select label='Estado inicial' value={form.status} onChange={handleChange('status')}>
                <MenuItem value={true}>Activo</MenuItem>
                <MenuItem value={false}>Inactivo</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Checkbox checked={form.rubricsCustomOrder} onChange={handleChange('rubricsCustomOrder')} sx={{ '&.Mui-checked': { color: PRIMARY_COLOR } }} />}
              label='Habilitar orden personalizado de rubros'
            />
          </Box>
        ) : step === 1 ? (
          <Box display='grid' gridTemplateColumns='1fr' gap={2} mt={1}>
            {warnNoRubrics && (
              <Alert severity='warning' onClose={() => setWarnNoRubrics(false)}>
                No se han agregado rubros. Puedes continuar de todas formas.
              </Alert>
            )}
            {(form.rubrics || []).length === 0 ? (
              <Box textAlign='center' py={3}>
                <Typography variant='body1' color='text.secondary'>No hay rubros agregados</Typography>
                <Typography variant='body2' color='text.secondary' mt={0.5}>
                  Agrega rubros usando el formulario de abajo
                </Typography>
              </Box>
            ) : (
              <Box display='grid' gridTemplateColumns='1fr' gap={1.5}>
                {(form.rubrics || []).map((rubric, index) => (
                  <Paper key={rubric.localId} variant='outlined' sx={{ p: 2 }}>
                    <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                      <Typography variant='caption' color='text.secondary' fontWeight={600}>Rubro {index + 1}</Typography>
                      <IconButton size='small' color='error' onClick={() => removeRubric(rubric.localId)}>
                        <Delete fontSize='small' />
                      </IconButton>
                    </Box>
                    <Box display='grid' gridTemplateColumns='2fr 1fr' gap={1} mb={1}>
                      <TextField
                        label='Nombre'
                        value={rubric.name}
                        onChange={e => updateRubricField(rubric.localId, 'name', e.target.value)}
                        size='small'
                        fullWidth
                      />
                      <TextField
                        label='Puntaje máx.'
                        type='number'
                        value={rubric.maxPoints}
                        onChange={e => updateRubricField(rubric.localId, 'maxPoints', Number(e.target.value))}
                        size='small'
                        fullWidth
                      />
                    </Box>
                    <Box display='flex' flexWrap='wrap'>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size='small'
                            checked={Boolean(rubric.includedInSum)}
                            onChange={e => updateRubricField(rubric.localId, 'includedInSum', e.target.checked)}
                            sx={{ '&.Mui-checked': { color: PRIMARY_COLOR } }}
                          />
                        }
                        label={<Typography variant='caption'>Incluido en suma General</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size='small'
                            checked={Boolean(rubric.allowNegative)}
                            onChange={e => updateRubricField(rubric.localId, 'allowNegative', e.target.checked)}
                            sx={{ '&.Mui-checked': { color: PRIMARY_COLOR } }}
                          />
                        }
                        label={<Typography variant='caption'>Permite negativo</Typography>}
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            <Paper variant='outlined' sx={{ border: '2px dashed #bdbdbd', borderRadius: 2, p: 2, bgcolor: 'transparent' }}>
              <Typography variant='subtitle2' fontWeight={600} color='text.secondary' mb={1.5}>
                + Agregar rubro
              </Typography>
              <Box display='grid' gridTemplateColumns='2fr 1fr' gap={1} mb={1.5}>
                <TextField
                  label='Nombre del rubro'
                  value={rubricDraft.name}
                  onChange={e => setRubricDraft(curr => ({ ...curr, name: e.target.value }))}
                  size='small'
                  fullWidth
                />
                <TextField
                  label='Puntaje máx.'
                  type='number'
                  value={rubricDraft.maxPoints}
                  onChange={e => setRubricDraft(curr => ({ ...curr, maxPoints: e.target.value }))}
                  size='small'
                  fullWidth
                />
              </Box>
              <TextField
                label='Instrucciones (opcional)'
                value={rubricDraft.instructions}
                onChange={e => setRubricDraft(curr => ({ ...curr, instructions: e.target.value }))}
                multiline
                minRows={2}
                size='small'
                fullWidth
                sx={{ mb: 1.5 }}
              />
              <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1}>
                <Box display='flex' flexWrap='wrap'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={Boolean(rubricDraft.includedInSum)}
                        onChange={e => setRubricDraft(curr => ({ ...curr, includedInSum: e.target.checked }))}
                        sx={{ '&.Mui-checked': { color: PRIMARY_COLOR } }}
                      />
                    }
                    label={<Typography variant='caption'>Incluido en suma General</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={Boolean(rubricDraft.allowNegative)}
                        onChange={e => setRubricDraft(curr => ({ ...curr, allowNegative: e.target.checked }))}
                        sx={{ '&.Mui-checked': { color: PRIMARY_COLOR } }}
                      />
                    }
                    label={<Typography variant='caption'>Permite negativo</Typography>}
                  />
                </Box>
                <Button
                  variant='contained'
                  startIcon={<Add />}
                  onClick={addRubric}
                  disabled={!canAddRubric}
                  size='small'
                  sx={{ bgcolor: PRIMARY_COLOR, '&:hover': { bgcolor: '#0a5241' } }}
                >
                  Agregar rubro
                </Button>
              </Box>
            </Paper>
          </Box>
        ) : (
          <Box display='grid' gridTemplateColumns='1fr' gap={2} mt={1}>
            <Typography variant='subtitle1' fontWeight={700}>Resumen del evento</Typography>
            <Paper variant='outlined' sx={{ p: 2 }}>
              <Box display='grid' gridTemplateColumns='1fr 1fr' gap={1.5}>
                <Box>
                  <Typography variant='caption' color='text.secondary'>Nombre</Typography>
                  <Typography variant='body2' fontWeight={600}>{form.name}</Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>Tipo de evento</Typography>
                  <Typography variant='body2' fontWeight={600}>{eventTypeName}</Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>Categoría</Typography>
                  <Typography variant='body2' fontWeight={600}>{eventCategoryName}</Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>Tipo de evaluación</Typography>
                  <Typography variant='body2' fontWeight={600}>{evaluationTypeName}</Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>Máximo de puntos</Typography>
                  <Typography variant='body2' fontWeight={600}>{form.maxPointEvents}</Typography>
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary'>Estado inicial</Typography>
                  <Box mt={0.5}>
                    <Chip label={form.status ? 'Activo' : 'Inactivo'} color={form.status ? 'success' : 'default'} size='small' />
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Typography variant='subtitle1' fontWeight={700}>
              Rubros ({(form.rubrics || []).length})
            </Typography>
            {(form.rubrics || []).length === 0 ? (
              <Alert severity='info'>No se agregarán rubros iniciales al evento.</Alert>
            ) : (
              <>
                <Box display='flex' flexWrap='wrap' gap={1}>
                  {(form.rubrics || []).map(r => (
                    <Chip
                      key={r.localId}
                      label={`${r.name} — ${r.maxPoints} pts`}
                      variant='outlined'
                      size='small'
                      icon={r.includedInSum ? <CheckCircle sx={{ fontSize: '14px !important' }} /> : undefined}
                    />
                  ))}
                </Box>
                {!rubricTotalValid && hasIncluded && (
                  <Alert severity='error'>
                    La suma de rubros incluidos ({includedSum}) no coincide con el máximo del evento ({Number(form.maxPointEvents || 0)}). Regresa al paso anterior para corregir.
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      {!savedOk && (
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleCancel} disabled={saving}>Cancelar</Button>
          <Box display='flex' gap={1}>
            {step > 0 && (
              <Button onClick={handleBack} disabled={saving}>← Atrás</Button>
            )}
            {step < 2 ? (
              <Button
                variant='contained'
                onClick={handleNext}
                disabled={saving}
                sx={{ bgcolor: PRIMARY_COLOR, '&:hover': { bgcolor: '#0a5241' } }}
              >
                Continuar →
              </Button>
            ) : (
              <Button
                variant='contained'
                onClick={handleSave}
                disabled={!canSaveForm || saving}
                sx={{ bgcolor: PRIMARY_COLOR, '&:hover': { bgcolor: '#0a5241' } }}
              >
                {saving ? 'Guardando...' : 'Guardar evento'}
              </Button>
            )}
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

const RubricDialog = ({ open, value, onClose, onSave, saving }) => {
  const [form, setForm] = useState(value);
  const isMobile = useIsMobile();

  useEffect(() => {
    setForm(value);
  }, [value]);

  const canSave = useMemo(() => {
    return form.name.trim().length > 1
      && String(form.maxPoints).trim() !== ''
      && Number.isFinite(Number(form.maxPoints));
  }, [form]);

  const handleChange = key => event => {
    const val = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(curr => ({ ...curr, [key]: val }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm' fullScreen={isMobile}>
      <DialogTitle>{form._id ? 'Editar rubro' : 'Agregar rubro'}</DialogTitle>
      <DialogContent dividers>
        <Box display='grid' gridTemplateColumns='1fr' gap={2} mt={1}>
          <TextField
            label='Nombre del rubro'
            value={form.name}
            onChange={handleChange('name')}
            required
            fullWidth
          />
          <TextField
            label='Puntaje maximo'
            type='number'
            value={form.maxPoints}
            onChange={handleChange('maxPoints')}
            required
            fullWidth
          />
          <FormControlLabel
            control={<Checkbox checked={Boolean(form.includedInSum)} onChange={handleChange('includedInSum')} />}
            label='Incluir en suma total'
          />
          <FormControlLabel
            control={<Checkbox checked={Boolean(form.allowNegative)} onChange={handleChange('allowNegative')} />}
            label='Permite puntaje negativo'
          />
          <TextField
            label='Instrucciones'
            value={form.instructions}
            onChange={handleChange('instructions')}
            multiline
            minRows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          onClick={() => onSave(form)}
          variant='contained'
          disabled={!canSave || saving}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EventRow = ({ event, expanded, onToggle, onRefresh, onEdit }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [busyAction, setBusyAction] = useState(false);

  const handleMenuClick = e => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleUpdateStatus = async () => {
    setBusyAction(true);
    handleMenuClose();

    try {
      await axios.put(`${eventUrl.BASE_URL}/${event._id}`, {
        status: !event.status,
      });
      await onRefresh();
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('No se pudo actualizar el estado del evento.');
    } finally {
      setBusyAction(false);
    }
  };

  const handleUnassignUsers = async () => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`Se removeran los usuarios asignados en los rubros de "${event.name}". Deseas continuar?`);
    if (!confirmed) {
      handleMenuClose();
      return;
    }

    setBusyAction(true);
    handleMenuClose();

    try {
      await axios.put(`${eventUrl.BASE_URL}/${event._id}/unassign-users`);
      await onRefresh();
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('No se pudo completar la desasignacion de usuarios.');
    } finally {
      setBusyAction(false);
    }
  };

  const rubricSum = (event.rubrics || [])
    .filter(r => r.includedInSum)
    .reduce((sum, rubric) => sum + Number(rubric.maxPoints || 0), 0);

  const isValid = rubricSum === Number(event.maxPointEvents || 0);

  return (
    <>
      <ExpandableRow>
        <TableCell>
          <IconButton size='small' onClick={onToggle}>
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{event.name}</TableCell>
        <TableCell>{event.event_type?.name || '-'}</TableCell>
        <TableCell>{event.evaluation_type?.name || '-'}</TableCell>
        <TableCell>{event.event_category?.name || '-'}</TableCell>
        <TableCell>
          {event.maxPointEvents}
          <ValidationIcon>
            {isValid ? (
              <Tooltip title='La suma de rubros coincide con el maximo del evento'>
                <CheckCircle color='success' fontSize='small' />
              </Tooltip>
            ) : (
              <Tooltip title={`Suma de rubros: ${rubricSum}, Maximo del evento: ${event.maxPointEvents}`}>
                <Warning color='warning' fontSize='small' />
              </Tooltip>
            )}
          </ValidationIcon>
        </TableCell>
        <TableCell>
          <Chip
            label={event.status ? 'Activo' : 'Inactivo'}
            color={event.status ? 'success' : 'default'}
            size='small'
          />
        </TableCell>
        <TableCell align='right'>
          <IconButton onClick={handleMenuClick} disabled={busyAction}>
            <MoreVert />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                onEdit(event);
              }}
            >
              Editar evento
            </MenuItem>
            <MenuItem onClick={handleUpdateStatus}>{event.status ? 'Desactivar evento' : 'Activar evento'}</MenuItem>
            <MenuItem onClick={handleUnassignUsers}>Desasignar usuarios de rubros</MenuItem>
          </Menu>
        </TableCell>
      </ExpandableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <Box margin={2}>
              <RubricsManager event={event} onRefresh={onRefresh} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const RubricActionsMenu = ({ onEdit, onDelete, onEditJudges }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const close = () => setAnchorEl(null);

  return (
    <>
      <IconButton size='small' aria-label='Acciones de rubro' onClick={e => setAnchorEl(e.currentTarget)}>
        <MoreVert fontSize='small' />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <MenuItem onClick={() => { close(); onEdit(); }}>Editar rubro</MenuItem>
        <MenuItem onClick={() => { close(); onEditJudges(); }}>
          <Box display='flex' alignItems='center' gap={1}>
            <People fontSize='small' />
            Editar jueces
          </Box>
        </MenuItem>
        <MenuItem onClick={() => { close(); onDelete(); }} sx={{ color: 'error.main' }}>Eliminar rubro</MenuItem>
      </Menu>
    </>
  );
};

const JudgeRemoveRow = ({ judge, onRemove, onRestore, isPending }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        py={0.75}
        px={1.5}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          transition: 'background-color 0.2s',
          backgroundColor: isPending
            ? 'rgba(239, 68, 68, 0.08)'
            : hovered
            ? 'rgba(0, 0, 0, 0.03)'
            : '#ffffff',
        }}
      >
        <Typography
          variant='body2'
          sx={{
            opacity: isPending ? 0.5 : 1,
            textDecoration: isPending ? 'line-through' : 'none',
            transition: 'opacity 0.2s',
            flex: 1,
            mr: 1,
          }}
        >
          {judge.fullname}
          <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
            ({judge.username})
          </Typography>
        </Typography>
        <Tooltip title={isPending ? 'Deshacer' : 'Quitar juez'}>
          <IconButton
            size='small'
            onClick={isPending ? onRestore : onRemove}
            sx={{
              color: isPending
                ? '#284B32'
                : hovered ? 'error.main' : 'text.disabled',
              transition: 'color 0.2s',
            }}
          >
            {isPending ? <Undo fontSize='small' /> : <DeleteOutline fontSize='small' />}
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ height: '1px', backgroundColor: 'rgba(0, 0, 0, 0.06)', mx: 1.5 }} />
    </>
  );
};

const EditJudgesDialog = ({ open, rubric, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [judges, setJudges] = useState([]);
  const [pendingRemoval, setPendingRemoval] = useState(new Set());

  useEffect(() => {
    if (!open || !rubric?._id) return;
    setLoading(true);
    setPendingRemoval(new Set());
    axios.get(`${rubricUrl.BASE_URL}/${rubric._id}`)
      .then(res => setJudges(res.data?.assignedJudge || []))
      .catch(() => setJudges([]))
      .finally(() => setLoading(false));
  }, [open, rubric?._id]);

  const handleRemove = id => setPendingRemoval(prev => new Set(prev).add(id));

  const handleRestore = id => {
    setPendingRemoval(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (pendingRemoval.size === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await axios.delete(`${rubricUrl.BASE_URL}/removeJudge`, {
        data: { rubricId: rubric._id, userIds: Array.from(pendingRemoval) },
      });
      onSaved();
      onClose();
    } catch {
      // eslint-disable-next-line no-alert
      alert('No se pudo guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth='xs' fullWidth>
      <DialogTitle>Editar jueces — {rubric?.name}</DialogTitle>
      <DialogContent dividers sx={{ p: 0, overflowY: 'auto' }}>
        {loading ? (
          <Box display='flex' justifyContent='center' py={3} px={2}>
            <CircularProgress size={28} />
          </Box>
        ) : judges.length === 0 ? (
          <Typography variant='body2' color='text.secondary' textAlign='center' py={2} px={2}>
            No hay jueces asignados a este rubro.
          </Typography>
        ) : (
          <>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', px: 1.5, pt: 1, pb: 0.5 }}>
              Haz clic en el ícono para quitar un juez. Los cambios se aplican al guardar.
            </Typography>
            {[...judges]
              .sort((a, b) => a.fullname.localeCompare(b.fullname))
              .map(judge => {
                const id = String(judge._id);
                return (
                  <JudgeRemoveRow
                    key={id}
                    judge={judge}
                    isPending={pendingRemoval.has(id)}
                    onRemove={() => handleRemove(id)}
                    onRestore={() => handleRestore(id)}
                  />
                );
              })}
          </>
        )}
      </DialogContent>
      {pendingRemoval.size > 0 && (
        <Alert severity='warning' sx={{ mx: 2, my: 1, borderRadius: 1 }}>
          {pendingRemoval.size} jue{pendingRemoval.size !== 1 ? 'ces' : 'z'} ser{pendingRemoval.size !== 1 ? 'án' : 'á'} removido{pendingRemoval.size !== 1 ? 's' : ''} al guardar.
        </Alert>
      )}
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={loading || saving}
          sx={{ bgcolor: PRIMARY_COLOR, '&:hover': { bgcolor: '#0a5241' } }}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RubricsManager = ({ event, onRefresh }) => {
  const isMobile = useIsMobile();
  const [rubricDialogOpen, setRubricDialogOpen] = useState(false);
  const [rubricSaving, setRubricSaving] = useState(false);
  const [rubricDraft, setRubricDraft] = useState({
    name: '',
    maxPoints: '',
    includedInSum: true,
    allowNegative: false,
    instructions: '',
  });
  const [editJudgesOpen, setEditJudgesOpen] = useState(false);
  const [editJudgesRubric, setEditJudgesRubric] = useState(null);

  const openEditJudges = rubric => {
    setEditJudgesRubric(rubric);
    setEditJudgesOpen(true);
  };

  const rubrics = normalizeRubrics(event.rubrics || [], Boolean(event.rubricsCustomOrder));

  const openCreateRubric = () => {
    setRubricDraft({
      name: '',
      maxPoints: '',
      includedInSum: true,
      allowNegative: false,
      instructions: '',
    });
    setRubricDialogOpen(true);
  };

  const openEditRubric = rubric => {
    setRubricDraft({
      ...rubric,
      maxPoints: rubric.maxPoints,
      instructions: rubric.instructions || '',
    });
    setRubricDialogOpen(true);
  };

  const persistOrdering = async orderedRubrics => {
    await Promise.all(
      orderedRubrics.map((item, index) => axios.put(`${rubricUrl.BASE_URL}/${item._id}/details`, {
        displayedOrder: index + 1,
      }))
    );
  };

  const handleMoveRubric = async (index, direction) => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= rubrics.length) return;

    const reordered = [...rubrics];
    const current = reordered[index];
    reordered[index] = reordered[targetIndex];
    reordered[targetIndex] = current;

    try {
      await persistOrdering(reordered);
      await onRefresh();
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('No se pudo guardar el orden de los rubros.');
    }
  };

  const handleSaveRubric = async data => {
    const campId = getCampIdFromEvent(event);

    if (!campId) {
      // eslint-disable-next-line no-alert
      alert('No se pudo identificar el campamento del evento.');
      return;
    }

    setRubricSaving(true);
    try {
      if (data._id) {
        await axios.put(`${rubricUrl.BASE_URL}/${data._id}/details`, {
          name: data.name.trim(),
          maxPoints: Number(data.maxPoints),
          includedInSum: Boolean(data.includedInSum),
          allowNegative: Boolean(data.allowNegative),
          instructions: data.instructions || '',
          displayedOrder: Number.isInteger(data.displayedOrder) ? data.displayedOrder : undefined,
        });
      } else {
        const nextOrder = event.rubricsCustomOrder
          ? rubrics.length + 1
          : -999;

        await axios.post(rubricUrl.BASE_URL, {
          name: data.name.trim(),
          maxPoints: Number(data.maxPoints),
          includedInSum: Boolean(data.includedInSum),
          allowNegative: Boolean(data.allowNegative),
          instructions: data.instructions || '',
          displayedOrder: nextOrder,
          event: event._id,
          camp: campId,
          assignedJudge: [],
        });
      }

      setRubricDialogOpen(false);
      await onRefresh();
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('No se pudo guardar el rubro.');
    } finally {
      setRubricSaving(false);
    }
  };

  const handleDeleteRubric = async rubric => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(`El rubro "${rubric.name}" sera eliminado de forma logica. Deseas continuar?`);
    if (!confirmed) return;

    try {
      await axios.delete(`${rubricUrl.BASE_URL}/${rubric._id}`);
      await onRefresh();
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('No se pudo eliminar el rubro.');
    }
  };

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={1} gap={2} flexWrap='wrap'>
        <Typography variant='subtitle1' fontWeight={700}>Rubros del evento</Typography>
        <Button variant='outlined' size='small' startIcon={<Add />} onClick={openCreateRubric}>
          Agregar rubro
        </Button>
      </Box>

      {/* Mobile: rubric cards */}
      {isMobile ? (
        <Stack spacing={1}>
          {rubrics.length === 0 ? (
            <Typography variant='body2' color='text.secondary' textAlign='center' py={2}>
              No hay rubros para este evento
            </Typography>
          ) : rubrics.map((rubric, index) => (
            <Paper key={rubric._id} variant='outlined' sx={{ p: 1.5 }}>
              <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
                <Box flex={1} mr={1}>
                  <Typography variant='body2' fontWeight={600}>{rubric.name}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Máx. {rubric.maxPoints} pts
                    {rubric.includedInSum ? ' · En suma' : ''}
                    {rubric.allowNegative ? ' · Negativo' : ''}
                  </Typography>
                </Box>
                <Box display='flex' alignItems='center' gap={0.5}>
                  {event.rubricsCustomOrder && (
                    <>
                      <IconButton size='small' aria-label='Subir' onClick={() => handleMoveRubric(index, 'up')} disabled={index === 0}>
                        <ArrowUpward fontSize='inherit' />
                      </IconButton>
                      <IconButton size='small' aria-label='Bajar' onClick={() => handleMoveRubric(index, 'down')} disabled={index === rubrics.length - 1}>
                        <ArrowDownward fontSize='inherit' />
                      </IconButton>
                    </>
                  )}
                  <RubricActionsMenu
                    onEdit={() => openEditRubric(rubric)}
                    onDelete={() => handleDeleteRubric(rubric)}
                    onEditJudges={() => openEditJudges(rubric)}
                  />
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        /* Desktop: table */
        <RubricsTable size='small'>
          <TableHead>
            <TableRow>
              <TableCell width={80}><strong>Ord.</strong></TableCell>
              <TableCell><strong>Nombre</strong></TableCell>
              <TableCell width={140}><strong>Max.</strong></TableCell>
              <TableCell width={160}><strong>Incluye suma</strong></TableCell>
              <TableCell width={160}><strong>Permite negativo</strong></TableCell>
              {event.rubricsCustomOrder ? <TableCell width={140}><strong>Orden</strong></TableCell> : null}
              <TableCell align='right' width={140}><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rubrics.length === 0 ? (
              <TableRow>
                <TableCell colSpan={event.rubricsCustomOrder ? 7 : 6} align='center'>
                  No hay rubros para este evento
                </TableCell>
              </TableRow>
            ) : rubrics.map((rubric, index) => (
              <TableRow key={rubric._id}>
                <TableCell>
                  {event.rubricsCustomOrder
                    ? (Number.isInteger(rubric.displayedOrder) ? rubric.displayedOrder : index + 1)
                    : index + 1}
                </TableCell>
                <TableCell>{rubric.name}</TableCell>
                <TableCell>{rubric.maxPoints}</TableCell>
                <TableCell>
                  <Checkbox checked={Boolean(rubric.includedInSum)} disabled />
                </TableCell>
                <TableCell>
                  <Checkbox checked={Boolean(rubric.allowNegative)} disabled />
                </TableCell>
                {event.rubricsCustomOrder ? (
                  <TableCell>
                    <Box display='flex' gap={0.5}>
                      <IconButton size='small' onClick={() => handleMoveRubric(index, 'up')} disabled={index === 0}>
                        <ArrowUpward fontSize='inherit' />
                      </IconButton>
                      <IconButton size='small' onClick={() => handleMoveRubric(index, 'down')} disabled={index === rubrics.length - 1}>
                        <ArrowDownward fontSize='inherit' />
                      </IconButton>
                    </Box>
                  </TableCell>
                ) : null}
                <TableCell align='right'>
                  <RubricActionsMenu
                    onEdit={() => openEditRubric(rubric)}
                    onDelete={() => handleDeleteRubric(rubric)}
                    onEditJudges={() => openEditJudges(rubric)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </RubricsTable>
      )}

      <RubricDialog
        open={rubricDialogOpen}
        value={rubricDraft}
        onClose={() => setRubricDialogOpen(false)}
        onSave={handleSaveRubric}
        saving={rubricSaving}
      />

      <EditJudgesDialog
        open={editJudgesOpen}
        rubric={editJudgesRubric}
        onClose={() => setEditJudgesOpen(false)}
        onSaved={onRefresh}
      />
    </>
  );
};

const ImportDialog = ({ open, parsed, errors, onClose, onConfirm, importing }) => {
  const isMobile = useIsMobile();
  return (
  <Dialog open={open} onClose={importing ? undefined : onClose} maxWidth='sm' fullWidth fullScreen={isMobile}>
    <DialogTitle>Importar eventos</DialogTitle>
    <DialogContent dividers>
      <Box display='grid' gap={2}>
        {errors.length > 0 && (
          <Alert severity='warning'>
            <Typography variant='body2' fontWeight={600} mb={0.5}>
              Los siguientes eventos no pudieron resolverse y serán omitidos:
            </Typography>
            <Box component='ul' pl={2} m={0}>
              {errors.map((err, i) => (
                <Typography key={i} component='li' variant='body2'>{err}</Typography>
              ))}
            </Box>
          </Alert>
        )}
        {parsed.length > 0 ? (
          <Alert severity='success'>
            Se importarán <strong>{parsed.length}</strong> evento{parsed.length !== 1 ? 's' : ''} al campamento actual.
          </Alert>
        ) : (
          <Alert severity='error'>No hay eventos válidos para importar.</Alert>
        )}
        {parsed.length > 0 && (
          <Box display='flex' flexWrap='wrap' gap={1}>
            {parsed.map((e, i) => (
              <Chip key={i} label={`${e.name} (${(e.rubrics || []).length} rubros)`} size='small' variant='outlined' />
            ))}
          </Box>
        )}
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={importing}>Cancelar</Button>
      <Button
        variant='contained'
        onClick={onConfirm}
        disabled={parsed.length === 0 || importing}
        sx={{ bgcolor: PRIMARY_COLOR, '&:hover': { bgcolor: '#0a5241' } }}
      >
        {importing ? 'Importando...' : `Importar ${parsed.length} evento${parsed.length !== 1 ? 's' : ''}`}
      </Button>
    </DialogActions>
  </Dialog>
  );
};

export const EventTableWithRubrics = () => {
  const { activeCamp } = useAuthContext();
  const campId = activeCamp?._id;
  const isMobile = useIsMobile();

  const [expandedRows, setExpandedRows] = useState({});
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const [eventTypes, setEventTypes] = useState([]);
  const [evaluationTypes, setEvaluationTypes] = useState([]);
  const [eventCategories, setEventCategories] = useState([]);

  const importFileRef = useRef(null);
  const pollingRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [generateGrades] = useGenerateGradesMutation();
  const { data: genStatus, refetch: refetchGenStatus } = useGetGenerateGradesStatusQuery(
    { campId },
    { skip: !campId }
  );

  useEffect(() => {
    if (genStatus?.generating) setGenerating(true);
  }, [genStatus?.generating]);

  useEffect(() => () => clearInterval(pollingRef.current), []);

  const handleGenerateGrades = async () => {
    try {
      await generateGrades({ campId }).unwrap();
      setGenerating(true);
      pollingRef.current = setInterval(async () => {
        const result = await refetchGenStatus();
        if (!result.data?.generating) {
          setGenerating(false);
          clearInterval(pollingRef.current);
        }
      }, 10000);
    } catch (err) {
      if (err.status === 409) setGenerating(true);
    }
  };

  const [exporting, setExporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importParsed, setImportParsed] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [dialogSaving, setDialogSaving] = useState(false);
  const [eventDraft, setEventDraft] = useState({
    name: '',
    event_type: '',
    evaluation_type: '',
    event_category: '',
    maxPointEvents: 0,
    status: true,
    rubricsCustomOrder: false,
  });

  const loadCatalogs = async () => {
    setCatalogLoading(true);
    try {
      const [eventTypeRes, evaluationRes, categoriesRes] = await Promise.all([
        axios.get(evenTypeUrl.GET_EVENTS_TYPES),
        axios.get(`${baseUrls.baseUrl}/evaluation-type`),
        axios.get(`${baseUrls.baseUrl}/event-category`),
      ]);

      const byName = (a, b) => a.name.localeCompare(b.name);
      setEventTypes((eventTypeRes.data || []).slice().sort(byName));
      setEvaluationTypes((evaluationRes.data || []).slice().sort(byName));
      setEventCategories((categoriesRes.data || []).slice().sort(byName));
    } catch (error) {
      setEventTypes([]);
      setEvaluationTypes([]);
      setEventCategories([]);
    } finally {
      setCatalogLoading(false);
    }
  };

  const fetchEvents = useCallback(async () => {
    if (!campId) {
      setEvents([]);
      setTotalEvents(0);
      setTotalPages(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${eventUrl.BASE_URL}/camp/${campId}/rubrics`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
        },
      });

      setEvents(response.data?.results || []);
      setTotalEvents(response.data?.pagination?.total || 0);
      setTotalPages(response.data?.pagination?.totalPages || 0);
    } catch (error) {
      setEvents([]);
      setTotalEvents(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [campId, page, rowsPerPage, search]);

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    setPage(0);
    setExpandedRows({});
    setSearchInput('');
    setSearch('');
  }, [campId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput]);

  useEffect(() => {
    const maxPage = Math.max(0, totalPages - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, totalPages]);

  const openCreateDialog = () => {
    setDialogMode('create');
    setEventDraft({
      name: '',
      event_type: '',
      evaluation_type: '',
      event_category: '',
      maxPointEvents: 0,
      status: true,
      rubricsCustomOrder: false,
      rubrics: [],
    });
    setDialogOpen(true);
  };

  const openEditDialog = event => {
    const includedRubrics = (event.rubrics || []).filter(item => item.includedInSum);
    const existingIncludedRubricsSum = includedRubrics.reduce((sum, item) => sum + Number(item.maxPoints || 0), 0);

    setDialogMode('edit');
    setEventDraft({
      _id: event._id,
      name: event.name || '',
      event_type: event.event_type?._id || event.event_type || '',
      evaluation_type: event.evaluation_type?._id || event.evaluation_type || '',
      event_category: event.event_category?._id || event.event_category || '',
      maxPointEvents: event.maxPointEvents ?? 0,
      status: Boolean(event.status),
      rubricsCustomOrder: Boolean(event.rubricsCustomOrder),
      includedRubricsCount: includedRubrics.length,
      existingIncludedRubricsSum,
    });
    setDialogOpen(true);
  };

  const handleSaveEvent = async form => {
    if (!campId) {
      // eslint-disable-next-line no-alert
      alert('Selecciona un campamento antes de crear o editar eventos.');
      return false;
    }

    setDialogSaving(true);

    try {
      if (dialogMode === 'create') {
        const payload = buildEventPayload(form, campId);

        const payloadRubrics = (form.rubrics || []).map((item, index) => ({
          name: item.name.trim(),
          maxPoints: Number(item.maxPoints),
          includedInSum: Boolean(item.includedInSum),
          allowNegative: Boolean(item.allowNegative),
          instructions: item.instructions || '',
          displayedOrder: payload.rubricsCustomOrder ? index + 1 : -999,
          assignedJudge: [],
        }));

        await axios.post(`${eventUrl.BASE_URL}/eventBulkCreation`, [{
          ...payload,
          rubrics: payloadRubrics,
        }]);
        await fetchEvents();
        return true;
      } else {
        const payload = buildEventPayload(form, campId);
        await axios.put(`${eventUrl.BASE_URL}/${form._id}`, payload);
        setDialogOpen(false);
        await fetchEvents();
        return true;
      }
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('No se pudo guardar el evento.');
      return false;
    } finally {
      setDialogSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${eventUrl.BASE_URL}/camp/${campId}/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      saveAs(blob, `eventos-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('No se pudo exportar los eventos.');
    } finally {
      setExporting(false);
    }
  };

  const handleImportFileSelect = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    try {
      const text = await file.text();
      const rawEvents = JSON.parse(text);

      if (!Array.isArray(rawEvents) || rawEvents.length === 0) {
        // eslint-disable-next-line no-alert
        alert('El archivo no contiene eventos válidos.');
        return;
      }

      const errors = [];
      const parsed = [];

      for (const event of rawEvents) {
        const eventTypeObj = eventTypes.find(t => t.name === event.event_type);
        const evalTypeObj = evaluationTypes.find(t => t.name === event.evaluation_type);
        const categoryObj = eventCategories.find(t => t.name === event.event_category);

        if (!eventTypeObj) errors.push(`"${event.name}": tipo de evento "${event.event_type}" no existe`);
        if (!evalTypeObj) errors.push(`"${event.name}": tipo de evaluación "${event.evaluation_type}" no existe`);
        if (!categoryObj) errors.push(`"${event.name}": categoría "${event.event_category}" no existe`);

        if (eventTypeObj && evalTypeObj && categoryObj) {
          parsed.push({
            name: event.name,
            event_type: eventTypeObj._id,
            evaluation_type: evalTypeObj._id,
            event_category: categoryObj._id,
            camp: campId,
            leadJudge: FIXED_LEAD_JUDGE_ID,
            maxPointEvents: Number(event.maxPointEvents),
            status: Boolean(event.status),
            rubricsCustomOrder: Boolean(event.rubricsCustomOrder),
            rubrics: (event.rubrics || []).map((r, index) => ({
              name: r.name,
              maxPoints: Number(r.maxPoints),
              includedInSum: Boolean(r.includedInSum),
              allowNegative: Boolean(r.allowNegative),
              instructions: r.instructions || '',
              displayedOrder: Number.isInteger(r.displayedOrder) ? r.displayedOrder : index + 1,
              assignedJudge: [],
            })),
          });
        }
      }

      setImportParsed(parsed);
      setImportErrors(errors);
      setImportDialogOpen(true);
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('Error al leer el archivo. Asegúrate de que sea un JSON válido.');
    }
  };

  const handleImportConfirm = async () => {
    setImporting(true);
    try {
      await axios.post(`${eventUrl.BASE_URL}/eventBulkCreation`, importParsed);
      await fetchEvents();
      setImportDialogOpen(false);
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('No se pudo importar los eventos.');
    } finally {
      setImporting(false);
    }
  };

  const toggleRow = eventId => {
    setExpandedRows(prev => ({
      ...prev,
      [eventId]: !prev[eventId],
    }));
  };

  if (!campId) {
    return (
      <StyledTableContainer component={Paper}>
        <EmptyState textAlign='center'>Selecciona un campamento para gestionar eventos</EmptyState>
      </StyledTableContainer>
    );
  }

  return (
    <StyledTableContainer component={Paper}>
      <Toolbar>
        <TextField
          label='Buscar por nombre de evento'
          value={searchInput}
          onChange={event => {
            setSearchInput(event.target.value);
            setPage(0);
          }}
          size='small'
          sx={{ minWidth: { xs: '100%', sm: 340 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search fontSize='small' />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position='end'>
                <CircularProgress size={18} />
              </InputAdornment>
            ) : null,
          }}
        />

        <Box display='flex' gap={1} flexWrap='wrap' alignItems='center' sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant='outlined'
            size='small'
            startIcon={<FileDownload />}
            onClick={handleExport}
            disabled={exporting || !campId}
            sx={{ flexShrink: 0 }}
          >
            {exporting ? 'Exportando...' : 'Exportar'}
          </Button>
          <Button
            variant='outlined'
            size='small'
            startIcon={<UploadFile />}
            onClick={() => importFileRef.current?.click()}
            disabled={importing || !campId}
            sx={{ flexShrink: 0 }}
          >
            Importar
          </Button>
          <input
            ref={importFileRef}
            type='file'
            accept='.json'
            style={{ display: 'none' }}
            onChange={handleImportFileSelect}
          />
          <Button
            variant='outlined'
            size='small'
            startIcon={generating ? <CircularProgress size={16} /> : <AutoFixHigh />}
            onClick={handleGenerateGrades}
            disabled={generating || !campId}
            sx={{ flexShrink: 0 }}
          >
            {generating ? 'Generando...' : 'Generar Rúbricas'}
          </Button>
          <Button
            variant='contained'
            size='small'
            startIcon={<Add />}
            onClick={openCreateDialog}
            sx={{ bgcolor: PRIMARY_COLOR, '&:hover': { bgcolor: '#0a5241' }, flexShrink: 0 }}
          >
            Nuevo evento
          </Button>
        </Box>
      </Toolbar>

      {catalogLoading ? (
        <Box px={2} pb={2}>
          <Alert severity='info'>Cargando catalogos para crear y editar eventos...</Alert>
        </Box>
      ) : null}

      {/* Mobile: card list */}
      {isMobile && (
        <Box px={2} pb={1}>
          {loading && events.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} sx={{ color: PRIMARY_COLOR }} />
            </Box>
          ) : events.length === 0 ? (
            <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 3 }}>
              No hay eventos para este campamento
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {events.map(event => (
                <Card key={event._id} variant='outlined'>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant='subtitle2' fontWeight={700} sx={{ flex: 1, mr: 1 }}>{event.name}</Typography>
                      <Chip
                        label={event.status ? 'Activo' : 'Inactivo'}
                        color={event.status ? 'success' : 'default'}
                        size='small'
                      />
                    </Box>
                    <Typography variant='caption' color='text.secondary' display='block'>
                      {event.event_type?.name || '—'} · {event.event_category?.name || '—'}
                    </Typography>
                    <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
                      {event.evaluation_type?.name || '—'} · Máx. {event.maxPointEvents} pts
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => openEditDialog(event)}
                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, color: PRIMARY_COLOR, borderColor: PRIMARY_COLOR }}
                      >
                        Editar
                      </Button>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => toggleRow(event._id)}
                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1 }}
                      >
                        {expandedRows[event._id] ? 'Ocultar rúbricas' : `Rúbricas (${(event.rubrics || []).length})`}
                      </Button>
                    </Box>
                    <Collapse in={Boolean(expandedRows[event._id])} timeout='auto' unmountOnExit>
                      <Box sx={{ mt: 1.5 }}>
                        <RubricsManager event={event} onRefresh={fetchEvents} />
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
          <TablePagination
            component='div'
            count={totalEvents}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={ev => { setRowsPerPage(parseInt(ev.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 100]}
          />
        </Box>
      )}

      {/* Desktop: Table */}
      {!isMobile && (
        <>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width='50px' />
            <TableCell><strong>Evento</strong></TableCell>
            <TableCell><strong>Tipo</strong></TableCell>
            <TableCell><strong>Tipo evaluacion</strong></TableCell>
            <TableCell><strong>Categoria</strong></TableCell>
            <TableCell><strong>Maximo</strong></TableCell>
            <TableCell><strong>Estado</strong></TableCell>
            <TableCell align='right'><strong>Acciones</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && events.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align='center'>Cargando eventos...</TableCell>
            </TableRow>
          ) : events.length > 0 ? (
            events.map(event => (
              <EventRow
                key={event._id}
                event={event}
                expanded={Boolean(expandedRows[event._id])}
                onToggle={() => toggleRow(event._id)}
                onRefresh={fetchEvents}
                onEdit={openEditDialog}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} align='center'>No hay eventos para este campamento</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        component='div'
        count={totalEvents}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={event => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 100]}
      />
        </>
      )}

      {dialogMode === 'create' ? (
        <CreateEventWizard
          open={dialogOpen}
          value={eventDraft}
          eventTypes={eventTypes}
          evaluationTypes={evaluationTypes}
          eventCategories={eventCategories}
          onClose={() => setDialogOpen(false)}
          onSave={handleSaveEvent}
          saving={dialogSaving}
        />
      ) : (
        <EventDialog
          open={dialogOpen}
          mode={dialogMode}
          value={eventDraft}
          eventTypes={eventTypes}
          evaluationTypes={evaluationTypes}
          eventCategories={eventCategories}
          onClose={() => setDialogOpen(false)}
          onSave={handleSaveEvent}
          saving={dialogSaving}
        />
      )}

      <ImportDialog
        open={importDialogOpen}
        parsed={importParsed}
        errors={importErrors}
        onClose={() => setImportDialogOpen(false)}
        onConfirm={handleImportConfirm}
        importing={importing}
      />
    </StyledTableContainer>
  );
};
